import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Svg, Typography } from '@altermaind/web/ui';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { useSkillsToBoost } from '../../hooks';
import { getTagsCall } from '../../state';

type Props = {
  onChangeTag: ({ tag }: { tag?: string }) => void;
};

export const FilterByTag: FC<Props> = ({ onChangeTag }) => {
  const t = useTranslations('trainingHardSkills.filtersAndOrderContent.tags');
  const { genericResponsiveValue } = useResponsiveStyle();
  const { isSkillToBoost } = useSkillsToBoost();

  const allSelectedLabel = t('all');
  const [activeChip, setActiveChip] = useState<string>(allSelectedLabel);
  const { execute: executeGetTags, tags } = getTagsCall.hook();

  const chipsList = useMemo(
    () => [
      { label: allSelectedLabel, isBoost: false },
      ...(tags?.map(tag => ({ label: tag.label, isBoost: isSkillToBoost(tag.label) })) || []),
    ],
    [tags, allSelectedLabel, isSkillToBoost],
  );

  const onChange = useCallback(
    (changedTag: string) => {
      setActiveChip(changedTag);
      if (changedTag === allSelectedLabel) {
        onChangeTag({ tag: 'all' });
        return;
      }
      const selectedTagId = tags?.find(selectedTag => selectedTag.label === changedTag)?.id;
      onChangeTag({ tag: selectedTagId || undefined });
    },
    [allSelectedLabel, onChangeTag, tags],
  );

  useEffect(() => {
    executeGetTags({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!tags) {
    return null;
  }

  return (
    <Box>
      <Box
        overflowX={genericResponsiveValue({
          breakPoints: { desktop: 'auto' },
          defaultValue: 'scroll',
        })}
        mt='s'
        mb='m'
        display='flex'
        flexDirection='row'
      >
        {chipsList.map(chip => (
          <Box
            key={chip.label}
            style={{ cursor: 'pointer' }}
            onClick={() => onChange(chip.label)}
            backgroundColor={activeChip === chip.label ? 'primary' : 'grey/dark/background'}
            borderRadius='l'
            px='xs'
            py='xxxs'
            mr='xxs'
            textAlign={genericResponsiveValue({
              breakPoints: { desktop: 'left' },
              defaultValue: 'center',
            })}
            alignItems='center'
            display='flex'
            flex='0 0 auto'
          >
            <Box display='flex' alignItems='center'>
              {chip.isBoost && <Svg name='rocket' fill='white' size='xs' />}
              <Typography ml={chip.isBoost ? 'xxs' : undefined} variant='p2/light'>
                {chip.label}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
